import { PinnedSheet } from './SetSheet';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { alert, confirm, haptic } from '../lib/telegram';
import { useT } from '../lib/i18n';

/**
 * Tags, added and removed one tap at a time.
 *
 * A tag is the coach's own word for a group of their clients — never
 * translated, never seeded: a new coach starts with none and writes the
 * ones they actually use.
 *
 * Toggling writes immediately rather than on a Save button. The sheet is
 * a list of switches, and a switch that needs confirming is a switch
 * somebody will leave half-set.
 */

const BASE = import.meta.env.VITE_API_URL ?? '';
let authToken = '';
export const setTagToken = (t: string) => { authToken = t; };

async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    // No body, no content type: the server reads the header as a promise
    // of JSON and refuses the request when none arrives.
    headers: { ...(init.body == null ? {} : { 'content-type': 'application/json' }),
      authorization: `Bearer ${authToken}`, ...(init.headers ?? {}) },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? `http_${res.status}`);
  }
  return res.status === 204 ? (undefined as T) : res.json();
}

export interface Tag { id: string; name: string; color: string | null; members?: number }

export const TAG_COLOURS: [string, 'co.colour.teal' | 'co.colour.blue' | 'co.colour.amber'
  | 'co.colour.purple' | 'co.colour.red' | 'co.colour.grey'][] = [
  ['#0e9b8c', 'co.colour.teal'],
  ['#2f80ed', 'co.colour.blue'],
  ['#b3801d', 'co.colour.amber'],
  ['#7c5cd6', 'co.colour.purple'],
  ['#d64545', 'co.colour.red'],
  ['#5e6774', 'co.colour.grey'],
];

export function TagSheet({ client, tags, onClose, onChanged }: {
  client: { id: string; name: string; groups?: Tag[] };
  tags: Tag[];
  onClose: () => void;
  onChanged: () => void;
}) {
  const t = useT();
  const [on, setOn] = useState<Set<string>>(new Set((client.groups ?? []).map((g) => g.id)));
  const [draft, setDraft] = useState('');
  const [colour, setColour] = useState(TAG_COLOURS[0][0]);
  const [editing, setEditing] = useState<Tag | null>(null);
  const [busy, setBusy] = useState(false);

  async function toggle(tag: Tag) {
    haptic.tap();
    const next = new Set(on);
    if (next.has(tag.id)) next.delete(tag.id); else next.add(tag.id);
    setOn(next);
    try {
      await api(`/api/coach/clients/${client.id}/groups`, {
        method: 'PUT', body: JSON.stringify({ group_ids: [...next] }),
      });
      onChanged();
    } catch {
      haptic.error();
      setOn(new Set(on));
    }
  }

  async function create() {
    const name = draft.trim();
    if (!name || busy) return;
    setBusy(true);
    try {
      const made = await api<Tag>('/api/coach/groups', {
        method: 'POST', body: JSON.stringify({ name, color: colour }),
      });
      setDraft('');
      const next = new Set(on).add(made.id);
      setOn(next);
      await api(`/api/coach/clients/${client.id}/groups`, {
        method: 'PUT', body: JSON.stringify({ group_ids: [...next] }),
      });
      haptic.success();
      onChanged();
    } catch (err: any) {
      haptic.error();
      await alert(err.message === 'group_already_exists' ? t('co.tags.exists') : t('co.tags.failed'));
    } finally { setBusy(false); }
  }

  if (editing) {
    return (
      <TagEditor tag={editing}
        onClose={() => setEditing(null)}
        onChanged={() => { setEditing(null); onChanged(); }} />
    );
  }

  return createPortal(
    <div className="sheet" onClick={onClose}>
      <div className="sheet__panel sheet--rhythm" onClick={(e) => e.stopPropagation()}>
        <div>
          <div className="sheet__title">{t('co.tags.title', { name: client.name })}</div>
          <div className="card__when">{t('co.tags.hint')}</div>
        </div>

        <div className="taglist">
          {tags.map((g) => (
            <div className="taglist__row" key={g.id}>
              <button className="taglist__toggle" aria-pressed={on.has(g.id)}
                onClick={() => toggle(g)}>
                <i className="taglist__dot" style={{ background: g.color ?? 'var(--hint)' }} />
                <span className="taglist__name">{g.name}</span>
                <span className="taglist__count">{g.members ?? 0}</span>
                <i className={`taglist__check${on.has(g.id) ? ' taglist__check--on' : ''}`}
                  style={on.has(g.id) && g.color ? { background: g.color, borderColor: g.color } : undefined}
                  aria-hidden="true" />
              </button>
              <button className="taglist__edit" onClick={() => { haptic.tap(); setEditing(g); }}
                aria-label={t('co.tags.edit')}>⋯</button>
            </div>
          ))}
        </div>

        <div className="tagnew">
          <label className="mf__label" htmlFor="tag-new">{t('co.tags.new')}</label>
          <input id="tag-new" className="notearea" style={{ minHeight: 0 }}
            placeholder={t('co.tags.newPlaceholder')}
            value={draft} onChange={(e) => setDraft(e.target.value)} />
          <div className="tagnew__row">
            {TAG_COLOURS.map(([hex, key]) => (
              <button key={hex} className={`swatch${colour === hex ? ' swatch--on' : ''}`}
                style={{ background: hex }} aria-label={t(key)} aria-pressed={colour === hex}
                onClick={() => { haptic.tap(); setColour(hex); }} />
            ))}
            <button className="savebtn tagnew__add" disabled={!draft.trim() || busy} onClick={create}>
              {t('co.tags.add')}
            </button>
          </div>
        </div>

        <button className="paybar__opt" onClick={onClose}>{t('co.tags.done')}</button>
      </div>
    </div>,
    document.body,
  );
}

/**
 * Every tag the coach has, with its colour and how many clients carry
 * it. The same editor the sheet uses does the renaming, recolouring and
 * deleting, so there is one place where a tag's rules live.
 */
/**
 * Settings → «Метки»: the list in a sheet, opened from one row. Tap a
 * tag to rename, recolour or delete it; «+ Новая метка» adds one, saved
 * with the pinned «Сохранить».
 */
export function TagsSheet({ onClose }: { onClose: () => void }) {
  const t = useT();
  const [tags, setTags] = useState<Tag[] | null>(null);
  const [editing, setEditing] = useState<Tag | null>(null);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState('');
  const [colour, setColour] = useState(TAG_COLOURS[0][0]);
  const [busy, setBusy] = useState(false);

  const load = () => api<Tag[]>('/api/coach/groups').then(setTags).catch(() => setTags([]));
  useEffect(() => { load(); }, []);

  async function create() {
    const name = draft.trim();
    if (!name || busy) return;
    setBusy(true);
    try {
      await api('/api/coach/groups', { method: 'POST', body: JSON.stringify({ name, color: colour }) });
      haptic.success();
      setDraft('');
      setAdding(false);
      load();
    } catch (err: any) {
      haptic.error();
      await alert(err.message === 'group_already_exists' ? t('co.tags.exists') : t('co.tags.failed'));
    } finally { setBusy(false); }
  }

  return (
    <PinnedSheet title={t('co.set.titleTags')} onClose={onClose}
      footer={adding ? <>
        <button className="paybar__opt" onClick={() => { setAdding(false); setDraft(''); }}>{t('co.tags.cancel')}</button>
        <button className="savebtn" disabled={!draft.trim() || busy} onClick={create}>{t('co.tags.save')}</button>
      </> : <button className="savebtn" onClick={onClose}>{t('co.set.done')}</button>}>
      {!tags ? <div className="skeleton" /> : (
      <div className="setuptags">
        {tags.length === 0 && <div className="card__when">{t('co.tags.none')}</div>}
        {tags.map((g) => (
          <button key={g.id} className="setuptags__row"
            onClick={() => { haptic.tap(); setEditing(g); }}>
            <i className="taglist__dot" style={{ background: g.color ?? 'var(--hint)' }} />
            {/* A tag is the coach's own word: never translated. */}
            <span className="setuptags__name">{g.name}</span>
            <span className="setuptags__count">{g.members ?? 0}</span>
            <span className="setuptags__chev" aria-hidden="true">›</span>
          </button>
        ))}

        {adding ? (
          <div className="tagnew">
            <label className="mf__label" htmlFor="setup-tag-new">{t('co.tags.new')}</label>
            <input id="setup-tag-new" className="notearea" style={{ minHeight: 0 }}
              placeholder={t('co.tags.newPlaceholder')} value={draft} autoFocus
              onChange={(e) => setDraft(e.target.value)} />
            <div className="tagnew__row">
              {TAG_COLOURS.map(([hex, key]) => (
                <button key={hex} className={`swatch${colour === hex ? ' swatch--on' : ''}`}
                  style={{ background: hex }} aria-label={t(key)} aria-pressed={colour === hex}
                  onClick={() => { haptic.tap(); setColour(hex); }} />
              ))}
            </div>
          </div>
        ) : (
          <button className="setuptags__add" onClick={() => { haptic.tap(); setAdding(true); }}>
            {t('co.tags.newTag')}
          </button>
        )}
      </div>
      )}

      {editing && (
        <TagEditor tag={editing}
          onClose={() => setEditing(null)}
          onChanged={() => { setEditing(null); load(); }} />
      )}
    </PinnedSheet>
  );
}

/** Rename, recolour or delete one tag. */
export function TagEditor({ tag, onClose, onChanged }: {
  tag: Tag; onClose: () => void; onChanged: () => void;
}) {
  const t = useT();
  const [name, setName] = useState(tag.name);
  const [colour, setColour] = useState(tag.color ?? TAG_COLOURS[0][0]);
  const [busy, setBusy] = useState(false);

  async function save() {
    if (!name.trim() || busy) return;
    setBusy(true);
    try {
      await api(`/api/coach/groups/${tag.id}`, {
        method: 'PUT', body: JSON.stringify({ name: name.trim(), color: colour }),
      });
      haptic.success();
      onChanged();
    } catch (err: any) {
      haptic.error();
      await alert(err.message === 'group_already_exists' ? t('co.tags.exists') : t('co.tags.failed'));
    } finally { setBusy(false); }
  }

  /**
   * Deleting a tag somebody has is not the same as deleting an empty
   * one, so it does not ask the same question. The count is the part
   * that matters, and the message says what survives.
   */
  async function remove() {
    const n = tag.members ?? 0;
    const ok = await confirm(
      n > 0
        ? `${t('co.tags.deleteTitle', { tag: tag.name })}\n\n` +
          t('co.tags.deleteBody', { n, clients: t.word('co.tags.clients', n) })
        : t('co.tags.deleteEmpty'),
    );
    if (!ok) return;
    setBusy(true);
    try {
      await api(`/api/coach/groups/${tag.id}`, { method: 'DELETE' });
      haptic.success();
      onChanged();
    } catch {
      haptic.error();
      await alert(t('co.tags.failed'));
    } finally { setBusy(false); }
  }

  return (
    <PinnedSheet title={t('co.tags.edit')} onClose={onClose}
      footer={<>
        <button className="paybar__opt" onClick={onClose}>{t('co.tags.cancel')}</button>
        <button className="savebtn" disabled={busy || !name.trim()} onClick={save}>{t('co.tags.save')}</button>
      </>}>
        <label className="mf">
          <span className="mf__label">{t('co.tags.name')}</span>
          <input className="notearea" style={{ minHeight: 0 }} value={name}
            onChange={(e) => setName(e.target.value)} />
        </label>

        <div>
          <span className="mf__label">{t('co.tags.colour')}</span>
          <div className="tagnew__row" style={{ marginTop: 8 }}>
            {TAG_COLOURS.map(([hex, key]) => (
              <button key={hex} className={`swatch${colour === hex ? ' swatch--on' : ''}`}
                style={{ background: hex }} aria-label={t(key)} aria-pressed={colour === hex}
                onClick={() => { haptic.tap(); setColour(hex); }} />
            ))}
          </div>
        </div>

        <button className="card__cancel" style={{ display: 'block', margin: '18px auto 0' }} disabled={busy} onClick={remove}>
          {t('co.tags.delete')}
        </button>
    </PinnedSheet>
  );
}
